/**
 * Aktionen in Klartext.
 *
 * Der Regelkern liefert Aktionen als Daten. Hier bekommen sie einen deutschen Satz,
 * einen Preis und eine Dringlichkeit - und nur hier. Der Kern kennt keine Sprache.
 */
import { HOTELS } from '../core/data/hotels'
import { FELDER } from '../core/data/spielplan'
import type { Aktion, Zustand } from '../core/typen'

export interface Beschriftung {
  readonly text: string
  readonly preis: string
  readonly art: 'wichtig' | 'normal' | 'warnung'
}

export function geld(betrag: number): string {
  return `${betrag.toLocaleString('de-DE')} €`
}

function bauKosten(z: Zustand, hotel: keyof typeof HOTELS, anzahl: number): number {
  const b = z.besitz[hotel]
  if (!b) return 0
  let s = 0
  for (let i = 0; i < anzahl; i++) s += HOTELS[hotel].gebaeude[b.gebaeude + i]?.kosten ?? 0
  return s
}

export function beschrifte(z: Zustand, a: Aktion): Beschriftung {
  switch (a.art) {
    case 'wuerfeln':
      return { text: 'Würfeln und ziehen', preis: '', art: 'wichtig' }

    case 'uebernachten':
      return { text: 'Übernachten und bezahlen', preis: '', art: 'wichtig' }

    case 'grundstueck-kaufen':
      return {
        text: `Grundstück ${HOTELS[a.hotel].name} kaufen`,
        preis: geld(HOTELS[a.hotel].grundstueck),
        art: 'normal',
      }

    case 'zwangskauf':
      return {
        text: `${HOTELS[a.hotel].name} im Zwangsverkauf übernehmen`,
        preis: geld(HOTELS[a.hotel].zwangsverkauf),
        art: 'normal',
      }

    case 'baugenehmigung': {
      const kosten = bauKosten(z, a.hotel, a.anzahl)
      const was = a.anzahl === 1 ? 'ein Gebäude' : `${a.anzahl} Gebäude`
      return {
        text: `Baugenehmigung ${HOTELS[a.hotel].name}: ${was}`,
        preis: `${geld(kosten)} · Würfel entscheidet`,
        art: 'normal',
      }
    }

    case 'gratis-bauen':
      return {
        text: `${HOTELS[a.hotel].name} kostenlos ausbauen`,
        preis: 'geschenkt',
        art: 'wichtig',
      }

    case 'gratis-freizeitpark':
      return {
        text: `Freizeitpark ${HOTELS[a.hotel].name} kostenlos anlegen`,
        preis: 'geschenkt',
        art: 'wichtig',
      }

    case 'freizeitpark-kaufen':
      return {
        text: `Freizeitpark ${HOTELS[a.hotel].name} anlegen`,
        preis: geld(HOTELS[a.hotel].freizeitpark.kosten),
        art: 'normal',
      }

    case 'eingang-kaufen':
      return {
        text: `Eingang ${HOTELS[a.hotel].name} auf Feld ${a.feld}`,
        preis: geld(HOTELS[a.hotel].eingang),
        art: 'normal',
      }

    case 'gratis-eingang':
      return {
        text: `Eingang ${HOTELS[a.hotel].name} auf Feld ${a.feld}`,
        preis: 'geschenkt',
        art: 'wichtig',
      }

    case 'versteigern':
      return {
        text: `${HOTELS[a.hotel].name} versteigern`,
        preis: 'kein Mindestpreis',
        art: 'warnung',
      }

    case 'bieten':
      return { text: `${geld(a.betrag)} bieten`, preis: '', art: 'normal' }

    case 'passen':
      return { text: 'Passen', preis: '', art: 'normal' }

    case 'zug-beenden':
      return {
        text: z.rechte.nochmal ? 'Nochmal würfeln (Sechs gewürfelt)' : 'Zug beenden',
        preis: '',
        art: z.rechte.nochmal ? 'wichtig' : 'normal',
      }

    case 'zug-abgeben':
      return { text: 'Auf den Zusatzzug verzichten', preis: '', art: 'normal' }
  }
}

const FELDNAMEN: Record<string, string> = {
  start: 'Parkplatz',
  kauf: 'Kauf-Feld',
  bau: 'Baugenehmigung',
  'eingang-gratis': 'Ein Eingang kostenlos',
  'bauphase-gratis': 'Eine Bauphase kostenlos',
}

export function feldName(index: number): string {
  const f = FELDER[index]
  return f ? (FELDNAMEN[f.art] ?? f.art) : '-'
}

export function phasenText(z: Zustand): string {
  switch (z.phase) {
    case 'wuerfeln':
      return 'Am Zug'
    case 'uebernachten':
      return 'Übernachtung fällig'
    case 'handeln':
      return `Steht auf: ${feldName(z.spieler[z.amZug]!.feld)}`
    case 'schuld':
      return 'Zahlungsunfähig - Besitz versteigern'
    case 'auktion':
      return 'Versteigerung'
    case 'ende':
      return 'Partie beendet'
  }
}

/**
 * Die Aktion, die die Leertaste ausloest.
 *
 * Wuerfeln und Bezahlen sind immer gemeint. Sonst die erste als wichtig markierte
 * Aktion - das sind die geschenkten. Nur wenn ohnehin nichts anderes uebrig ist,
 * beendet die Leertaste den Zug; ein versehentlich beendeter Zug waere teuer.
 */
export function hauptAktion(z: Zustand, moeglich: readonly Aktion[]): Aktion | null {
  if (z.phase === 'auktion' || z.phase === 'ende') return null
  const wuerfeln = moeglich.find((a) => a.art === 'wuerfeln')
  if (wuerfeln) return wuerfeln
  const zahlen = moeglich.find((a) => a.art === 'uebernachten')
  if (zahlen) return zahlen
  const wichtig = moeglich.find(
    (a) =>
      beschrifte(z, a).art === 'wichtig' &&
      a.art !== 'eingang-kaufen' &&
      a.art !== 'gratis-eingang' &&
      a.art !== 'zug-beenden',
  )
  if (wichtig) return wichtig
  if (moeglich.length === 1 && moeglich[0]!.art === 'zug-beenden') return moeglich[0]
  return null
}
